/* eslint-disable react/no-unescaped-entities */
"use client";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, Mail, CheckCircle2 } from "lucide-react";
import toast from "react-hot-toast";

interface AskLocalFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export default function AskLocalFormModal({
  isOpen,
  onClose,
}: AskLocalFormModalProps) {
  const [step, setStep] = useState<"form" | "flow" | "verify">("form");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [focused, setFocused] = useState<string | null>(null);
  const [loading, setLoading] = useState(false); // ⭐ ADDED
  const [popup, setPopup] = useState(false); // ⭐ ADDED

  const [code, setCode] = useState(Array(6).fill(""));
  const [timer, setTimer] = useState(30);
  const API = process.env.NEXT_PUBLIC_API_URL; // e.g. http://localhost:5000
  // countdown for resend
  useEffect(() => {
    if (step === "verify" && timer > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [step, timer]);

  const handleCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value.replace(/\D/g, ""); // only digits

    // 👉 Paste full OTP (e.g. 269538)
    if (value.length === 6) {
      const pasted = value.split("").slice(0, 6);
      setCode(pasted);

      // focus last box
      document.getElementById("code-5")?.focus();

      // auto verify
      handleVerify(pasted.join(""));
      return;
    }

    if (!value) {
      const newCode = [...code];
      newCode[index] = "";
      setCode(newCode);
      return;
    }

    const newCode = [...code];
    newCode[index] = value[0];
    setCode(newCode);

    // move forward
    if (index < 5) {
      document.getElementById(`code-${index + 1}`)?.focus();
    }

    // auto verify when complete
    if (newCode.every((d) => d !== "")) {
      handleVerify(newCode.join(""));
    }
  };

  // Step 1 — user info
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Fire request but don't wait for email sending
    fetch(`${API}/api/v1/user-info`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ firstName, lastName, email }),
    }).finally(() => {
      setLoading(false);
      setStep("flow");
    });
  };

  // Step 2 — flow selection
  const handleFlowSelect = async (flowType: string) => {
    setLoading(true);

    fetch(`${API}/api/v1/update-flow`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, flowType }),
    }).finally(() => {
      setLoading(false);
      setStep("verify");
      setTimer(30);
    });
  };

  // Step 3 — verify
  const handleVerify = async (otp?: string) => {
    const finalCode = otp ?? code.join("");
    if (finalCode.length !== 6) return;
    console.log(finalCode, "code here");
    setLoading(true);

    const res = await fetch(`${API}/api/v1/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        code: finalCode,
      }),
    });

    const data = await res.json();
    setLoading(false);
    console.log(data);
    if (data.success) {
      console.log(data.success);
      setPopup(true);
      setTimeout(() => {
        setPopup(false);
        resetForm();
        onClose();
      }, 2500);
    } else {
      toast.error(data.message || "Invalid code");
    }
  };

  // resend
  const handleResend = async () => {
    if (timer > 0) return;

    setLoading(true);
    setCode(Array(6).fill(""));
    setTimer(30);

    await fetch(`${API}/api/v1/resend-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });

    setLoading(false);
  };
  const resetForm = () => {
    setStep("form");
    setFirstName("");
    setLastName("");
    setEmail("");
    setCode(Array(6).fill(""));
    setTimer(30);
    setFocused(null);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      document.getElementById(`code-${index - 1}`)?.focus();
    }
  };

  return (
    <>
      {popup && (
        <motion.div
          initial={{ opacity: 0, x: 50, y: -20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: 50, y: -20 }}
          transition={{ duration: 0.3 }}
          className="fixed top-5 right-5 z-9999"
        >
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 px-6 py-4 w-72">
            <h3 className="text-lg font-semibold text-[#4177BC]">
              You're All Set!
            </h3>
            <p className="text-gray-600 text-sm mt-1">
              We’ve sent a message to your email. Please check your inbox
              shortly.
            </p>
          </div>
        </motion.div>
      )}

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Background */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={onClose}
              className="fixed inset-0 backdrop-blur-sm bg-black/40 z-40"
            />

            {/* Modal */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 60 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 60 }}
              transition={{ type: "spring", stiffness: 200, damping: 22 }}
              className="fixed inset-0 flex items-center justify-center z-50 px-4"
            >
              <div className="relative w-full max-w-md p-8 md:p-10 rounded-2xl bg-white backdrop-blur-2xl border border-white/40 shadow-[0_10px_60px_rgba(0,0,0,0.3)]">
                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="absolute top-3 right-4 text-gray-400 hover:text-gray-700 text-2xl font-bold transition-all"
                >
                  ×
                </button>

                {/* Headers */}
                {step === "form" && (
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-semibold text-gray-900 mb-2">
                      Your Smarter Way to Real Estate
                    </h2>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Welcome to your AskLocal Concierge — empower your next
                      move with real insights, not sales pitches.
                    </p>
                  </div>
                )}

                {step === "flow" && (
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                      Choose Your Path
                    </h2>
                    <p className="text-gray-600 text-sm">
                      Tell us what you're exploring so we can personalize your
                      journey.
                    </p>
                  </div>
                )}

                {step === "verify" && (
                  <div className="text-center mb-8">
                    <CheckCircle2 className="mx-auto text-[#4177BC] w-12 h-12 mb-2" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-1">
                      Verification Sent!
                    </h2>
                    <p className="text-gray-600 text-sm">
                      We’ve sent a 6-digit code to <strong>{email}</strong>.
                      Enter it below to verify your email.
                    </p>
                  </div>
                )}

                {/* Step 1 — FORM */}
                {step === "form" && (
                  <form onSubmit={handleFormSubmit} className="space-y-6">
                    {/* First Name */}
                    <div className="relative">
                      <User
                        className={`absolute left-3 top-3.5 w-5 h-5 transition-colors ${
                          focused === "firstName"
                            ? "text-[#4177BC]"
                            : "text-gray-400"
                        }`}
                      />
                      <input
                        id="firstName"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        onFocus={() => setFocused("firstName")}
                        onBlur={() => setFocused(null)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg bg-white/70 focus:bg-white focus:border-[#4177BC] focus:ring-2 focus:ring-[#4177BC]/30 text-sm outline-none"
                        required
                      />
                      <label
                        htmlFor="firstName"
                        className={`absolute left-10 px-1 bg-white transition-all duration-200 ${
                          focused === "firstName" || firstName
                            ? "-top-2 text-xs text-[#4177BC]"
                            : "top-3.5 text-sm text-gray-400"
                        }`}
                      >
                        First Name
                      </label>
                    </div>

                    {/* Last Name */}
                    <div className="relative">
                      <User
                        className={`absolute left-3 top-3.5 w-5 h-5 transition-colors ${
                          focused === "lastName"
                            ? "text-[#4177BC]"
                            : "text-gray-400"
                        }`}
                      />
                      <input
                        id="lastName"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        onFocus={() => setFocused("lastName")}
                        onBlur={() => setFocused(null)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg bg-white/70 focus:bg-white focus:border-[#4177BC] focus:ring-2 focus:ring-[#4177BC]/30 text-sm outline-none"
                        required
                      />
                      <label
                        htmlFor="lastName"
                        className={`absolute left-10 px-1 bg-white transition-all duration-200 ${
                          focused === "lastName" || lastName
                            ? "-top-2 text-xs text-[#4177BC]"
                            : "top-3.5 text-sm text-gray-400"
                        }`}
                      >
                        Last Name
                      </label>
                    </div>

                    {/* Email */}
                    <div className="relative">
                      <Mail
                        className={`absolute left-3 top-3.5 w-5 h-5 transition-colors ${
                          focused === "email"
                            ? "text-[#4177BC]"
                            : "text-gray-400"
                        }`}
                      />
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onFocus={() => setFocused("email")}
                        onBlur={() => setFocused(null)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg bg-white/70 focus:bg-white focus:border-[#4177BC] focus:ring-2 focus:ring-[#4177BC]/30 text-sm outline-none"
                        required
                      />
                      <label
                        htmlFor="email"
                        className={`absolute left-10 px-1 bg-white transition-all duration-200 ${
                          focused === "email" || email
                            ? "-top-2 text-xs text-[#4177BC]"
                            : "top-3.5 text-sm text-gray-400"
                        }`}
                      >
                        Email Address
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-linear-to-r from-[#4177BC] to-[#5EA8E8] text-white py-3 rounded-lg text-sm font-semibold shadow-lg hover:-translate-y-0.5 transition-all"
                    >
                      {loading ? "Processing..." : "Access Your Concierge"}
                    </button>

                    <p className="text-xs text-gray-400 text-center mt-2">
                      🔒 No spam. No pressure. Just smart guidance built around
                      you.
                    </p>
                  </form>
                )}

                {/* STEP 2 — FLOW SELECTION */}
                {step === "flow" && (
                  <div className="flex flex-col gap-4">
                    <button
                      onClick={() => handleFlowSelect("buyer")}
                      disabled={loading}
                      className="w-full bg-[#4177BC] text-white py-3 rounded-xl inter-semibold shadow hover:bg-[#35649D] transition"
                    >
                      {loading ? "Please wait..." : "I'm a Buyer"}
                    </button>

                    <button
                      onClick={() => handleFlowSelect("seller")}
                      disabled={loading}
                      className="w-full bg-[#5EA8E8] text-white py-3 rounded-xl inter-semibold shadow hover:bg-[#4177BC] transition"
                    >
                      {loading ? "Please wait..." : "I'm a Seller"}
                    </button>

                    <button
                      onClick={() => handleFlowSelect("refinance")}
                      disabled={loading}
                      className="w-full bg-gray-200 text-gray-800 py-3 rounded-xl inter-semibold shadow hover:bg-gray-300 transition"
                    >
                      {loading ? "Please wait..." : "Refinance"}
                    </button>
                  </div>
                )}

                {/* STEP 3 — VERIFY */}
                {step === "verify" && (
                  <div className="flex flex-col items-center space-y-6">
                    <div className="flex justify-center gap-2">
                      {code.map((digit, i) => (
                        <input
                          key={i}
                          id={`code-${i}`}
                          type="text"
                          value={digit}
                          maxLength={1}
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          autoCorrect="off"
                          spellCheck={false}
                          onChange={(e) => handleCodeChange(e, i)}
                          onKeyDown={(e) => handleKeyDown(e, i)}
                          className="w-10 h-12 text-center text-lg font-semibold border rounded-lg focus:border-[#4177BC] focus:ring-2 focus:ring-[#4177BC]/30 transition"
                        />
                      ))}
                    </div>

                    <button
                      onClick={() => handleVerify()}
                      disabled={loading}
                      className="w-full bg-linear-to-r from-[#4177BC] to-[#5EA8E8] text-white py-3 rounded-lg font-semibold"
                    >
                      {loading ? "Verifying..." : "Verify Email"}
                    </button>

                    <button
                      onClick={handleResend}
                      disabled={timer > 0}
                      className="text-sm text-[#4177BC] hover:underline disabled:text-gray-400"
                    >
                      {timer > 0
                        ? `Resend code in ${timer}s`
                        : "Resend Verification Code"}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
